import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import { MarketplaceProvider, useMarketplace } from './MarketplaceContext';
import { FavoritesProvider } from './FavoritesContext';
import { MarketplaceCard } from './MarketplaceCard';

const useStyles = makeStyles((theme: Theme) => ({
  root: { minHeight: '100vh', background: theme.palette.background.default },
  header: {
    background: `linear-gradient(135deg, #00101F 0%, #001B3D 35%, #003075 65%, #0050B3 100%)`,
    padding: theme.spacing(4, 4, 3),
  },
  backBtn: {
    color: 'rgba(255,255,255,0.8)',
    marginBottom: theme.spacing(1),
    textTransform: 'none',
    fontSize: '0.85rem',
    padding: 0,
    '&:hover': { color: '#fff', background: 'none' },
  },
  pageTitle: { fontWeight: 800, fontSize: '2rem', color: '#fff' },
  pageDesc: {
    color: 'rgba(255,255,255,0.8)',
    marginTop: theme.spacing(0.5),
    fontSize: '0.95rem',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchField: {
    background: 'rgba(255,255,255,0.12)',
    borderRadius: theme.spacing(4),
    flex: '1 1 280px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(4),
      color: '#fff',
      '& fieldset': { border: '1.5px solid rgba(255,255,255,0.3)' },
      '&:hover fieldset': { border: '1.5px solid rgba(255,255,255,0.6)' },
      '&.Mui-focused fieldset': { border: '2px solid #fff' },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: 'rgba(255,255,255,0.6)',
    },
    '& input': { color: '#fff' },
  },
  selectRoot: {
    color: '#fff',
    '& .MuiOutlinedInput-root': { borderRadius: 20, color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.35)',
    },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
    minWidth: 160,
  },
  selectLabel: {
    color: 'rgba(255,255,255,0.7)',
    '&.Mui-focused': { color: 'rgba(255,255,255,0.9)' },
  },
  body: { maxWidth: 1200, margin: '0 auto', padding: theme.spacing(3, 3) },
  countLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(2),
  },
}));

function AgentsPageInner() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { agents, loading, error } = useMarketplace();

  const initialQuery = new URLSearchParams(location.search).get('q') ?? '';
  const [search, setSearch] = useState(initialQuery);
  const [filterKeyword, setFilterKeyword] = useState('');

  const allKeywords = useMemo(() => {
    const set = new Set<string>();
    agents.forEach(a => a.keywords.forEach(k => set.add(k)));
    return Array.from(set).sort();
  }, [agents]);

  const filtered = useMemo(() => {
    let result = agents;
    const q = search.toLowerCase();
    if (q) {
      result = result.filter(
        a =>
          a.displayName.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.keywords.some(k => k.toLowerCase().includes(q)),
      );
    }
    if (filterKeyword) {
      result = result.filter(a => a.keywords.includes(filterKeyword));
    }
    return result;
  }, [agents, search, filterKeyword]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Button
          startIcon={<ArrowBackIcon />}
          className={classes.backBtn}
          disableRipple
          onClick={() => navigate('/ai-marketplace')}
        >
          AI Marketplace
        </Button>
        <Typography className={classes.pageTitle}>Custom Agents</Typography>
        <Typography className={classes.pageDesc}>
          Install GitHub Copilot agents directly into VS Code or VS Code
          Insiders
        </Typography>
        <Box className={classes.filterBar}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search agents…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl
            variant="outlined"
            size="small"
            className={classes.selectRoot}
          >
            <InputLabel className={classes.selectLabel}>
              Filter by keyword
            </InputLabel>
            <Select
              value={filterKeyword}
              onChange={e => setFilterKeyword(e.target.value as string)}
              label="Filter by keyword"
            >
              <MenuItem value="">All</MenuItem>
              {allKeywords.map(k => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className={classes.body}>
        {loading && (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {!loading && !!error && (
          <Typography color="error">Failed to load: {error}</Typography>
        )}
        {!loading && !error && (
          <>
            <Typography className={classes.countLabel}>
              {filtered.length} of {agents.length} agents
            </Typography>
            <Grid container spacing={2}>
              {filtered.map(item => (
                <Grid item xs={12} sm={6} md={4} xl={3} key={item.id}>
                  <MarketplaceCard item={item} />
                </Grid>
              ))}
            </Grid>
            {filtered.length === 0 && (
              <Typography
                style={{ textAlign: 'center', opacity: 0.5, paddingTop: 40 }}
              >
                No agents match your search.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export function AgentsPage() {
  return (
    <MarketplaceProvider>
      <FavoritesProvider>
        <AgentsPageInner />
      </FavoritesProvider>
    </MarketplaceProvider>
  );
}
